'use client';

import { useState, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import BgOrbs from './BgOrbs';
import { tryCtaScrollToFinal } from '../lib/tryNavigation';

/**
 * SCENE 6: Категории. Clean-стиль с интерактивным mockup-каталогом.
 * Чипы кликабельные — выбор категории мгновенно меняет содержимое
 * сетки через AnimatePresence (mode="wait" — плавный переход old→new).
 */

type Item = { sender: string; text: string };
type Category = { emoji: string; name: string; items: Item[] };

const CATEGORIES: Category[] = [
  {
    emoji: '🎬',
    name: 'посмотреть',
    items: [
      { sender: 'Маша',          text: 'Форест Гамп (1994)' },
      { sender: 'Кино-чат',      text: '15 русских фильмов 2026' },
      { sender: 'Антон',         text: 'Severance s2' },
      { sender: 'TV-канал',      text: 'Новинки Apple TV+ весной' },
      { sender: 'Маша',          text: 'Дудь, новый выпуск' },
      { sender: 'Кино-чат',      text: 'Хичкок — Окно во двор' },
    ],
  },
  {
    emoji: '📚',
    name: 'почитать',
    items: [
      { sender: 'Книги до 30',   text: '«1984», Оруэлл' },
      { sender: 'Книги до 30',   text: '10 книг 2026' },
      { sender: 'Лена',          text: 'Тартт — «Маленький друг»' },
      { sender: 'TG-канал',      text: 'Статья про сон и продуктивность' },
    ],
  },
  {
    emoji: '🎵',
    name: 'послушать',
    items: [
      { sender: 'Антон',         text: 'Новый альбом ICEBOY' },
      { sender: 'Spotify',       text: 'Плейлист «весна 2026»' },
      { sender: 'Маша',          text: 'Подкаст «Запуск завтра»' },
      { sender: 'Лена',          text: 'Аудиокнига «Тёмные начала»' },
    ],
  },
  {
    emoji: '🍳',
    name: 'приготовить',
    items: [
      { sender: 'мама',          text: 'Блинчики на кефире' },
      { sender: 'мама',          text: 'Паста с лососем' },
      { sender: 'мама',          text: 'Пирог с яблоками' },
      { sender: 'мама',          text: 'Маринад для шашлыка' },
      { sender: 'TG-канал',      text: 'Хлеб без замеса' },
    ],
  },
  {
    emoji: '📍',
    name: 'поехать',
    items: [
      { sender: 'Лена',          text: 'Кофейня «Прокуратор»' },
      { sender: 'Лена',          text: 'Бар «Profsoyuz»' },
      { sender: 'Афиша',         text: 'Выставка в Гараже' },
      { sender: 'TG-канал',      text: 'Прогулка по Замоскворечью' },
    ],
  },
];

export default function Scene06Categories() {
  const reduced = useReducedMotion() ?? false;
  const [activeIdx, setActiveIdx] = useState(0);
  const active = CATEGORIES[activeIdx];

  const goToCovers = useCallback(() => {
    document.getElementById('scene-covers')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const goToFinal = useCallback(() => {
    tryCtaScrollToFinal();
  }, []);

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-[#f3fcf0] flex items-center">
      <BgOrbs variant="subtle" />

      <div className="relative max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-center px-5 sm:px-8 py-20 lg:py-24">
        {/* Левая колонка — текст */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-start gap-7"
        >
          <h2
            className="font-display font-bold text-[#161d16] leading-[0.98] tracking-[-0.02em]"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
          >
            Всё<br />
            <span className="text-[#b7102a]">по полочкам.</span>
          </h2>
          <p className="font-body text-[#4a5a49] text-base sm:text-lg leading-relaxed max-w-md">
            При каждом сохранении бот предлагает категорию. Фильмы — в «посмотреть», рецепты — в «приготовить», места — в «поехать». Ты только нажимаешь, куда добавить. Категории можно настроить по своему вкусу, а выбор категорий — автоматизировать.
          </p>
          <div className="flex flex-col items-start gap-5">
            <button
              type="button"
              onClick={goToFinal}
              className="group relative inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
              style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.45)' }}
            >
              <span>Попробовать сейчас</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={goToCovers}
              className="group inline-flex items-center gap-2 text-[#4a5a49] hover:text-[#161d16] font-body text-sm font-medium transition-colors"
            >
              <span>Что ещё?</span>
              <motion.svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                animate={reduced ? {} : { y: [0, 4, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </motion.svg>
            </button>
          </div>
        </motion.div>

        {/* Правая — интерактивный mockup */}
        <motion.div
          initial={reduced ? false : { opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div
            className="relative bg-white border border-[#dce5d9] rounded-[2rem] overflow-hidden"
            style={{ boxShadow: '0 32px 64px -24px rgba(20,40,20,0.18)' }}
          >
            {/* Mock window header */}
            <div className="flex items-center gap-1.5 px-5 py-3 border-b border-[#e7f0e5] bg-[#f7faf6]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#e57b7b]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#e6c47a]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#7ab07a]" />
              <span className="ml-3 font-mono text-[10px] text-[#7a8a79] uppercase tracking-widest">мой каталог</span>
            </div>

            {/* Кликабельные чипы */}
            <div className="px-5 py-4 border-b border-[#e7f0e5] flex flex-wrap gap-2">
              {CATEGORIES.map((c, i) => {
                const isActive = i === activeIdx;
                return (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body border transition-all ${
                      isActive
                        ? 'bg-[#161d16] text-white border-[#161d16] shadow-md'
                        : 'bg-white text-[#4a5a49] border-[#dce5d9] hover:border-[#7a8a79] hover:bg-[#f7faf6]'
                    }`}
                  >
                    <span className="text-sm leading-none">{c.emoji}</span>
                    {c.name}
                    <span className={`text-[10px] ${isActive ? 'text-white/60' : 'text-[#7a8a79]'}`}>
                      {c.items.length}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Сетка с переключением через AnimatePresence */}
            <div className="p-5 min-h-[260px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIdx}
                  initial={reduced ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduced ? undefined : { opacity: 0, y: -8 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                >
                  {active.items.map((it, i) => (
                    <motion.div
                      key={`${activeIdx}-${i}`}
                      initial={reduced ? false : { opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: reduced ? 0 : i * 0.04 }}
                      className="border border-[#e7f0e5] rounded-2xl p-3 hover:border-[#b8c8b6] transition-colors"
                    >
                      <div className="aspect-[4/3] rounded-xl bg-gradient-to-br from-[#e7f0e5] to-[#dce5d9] mb-2.5 flex items-center justify-center text-3xl">
                        {active.emoji}
                      </div>
                      <div className="font-body text-[#7a8a79] text-[10px] mb-0.5 truncate">{it.sender}</div>
                      <div className="font-body text-[#161d16] text-[13px] leading-snug line-clamp-2">{it.text}</div>
                    </motion.div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
